import React, { useCallback, useMemo } from 'react';

import { Tab, TabList } from '@rbilabs/components-library/build/components/tabs';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EVENT_PAGE_NAMES, EventTypes } from 'state/cdp/constants';
import { useLocationContext } from 'state/location';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

const Container = styled.div`
  display: block;
  width: 90%;
  max-width: 88rem;
  overflow: hidden;
  margin: 0 auto;

  button {
    font-size: 1rem;
  }

  button[aria-selected='true'] {
    color: ${({ theme: { token } }) => token('background-button-primary-default')};
    font-weight: bold;
  }

  ${Styles.desktop} {
    margin-block-start: 0;
    margin-block-end: 1.5rem;
    margin-inline: auto;
  }
`;

export type ILoyaltyTabSelectorWidgetProps = {
  activeTab?: number;
};

export enum LoyaltyTabOption {
  HOW_IT_WORKS,
  LOYALTY_HISTORY,
}

export const LoyaltyTabSelectorWidget: React.FC<ILoyaltyTabSelectorWidgetProps> = ({
  activeTab = LoyaltyTabOption.HOW_IT_WORKS,
  ...props
}) => {
  const navigate = useNavigate();
  const { setEventPageName } = useLocationContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { loading, data } = useFeaturesLoyaltyTiersContent();
  const location = useLocation();
  const cdp = useCdpContext();

  const loyaltyTabs = useMemo(
    () => [
      {
        link: data?.loyaltyTiersHowItWorksButtonLink?.locale,
        title: data?.loyaltyTiersHowItWorksButtonText?.locale,
      },
      {
        link: data?.loyaltyTiersRewardsHistoryButtonLink?.locale,
        title: data?.loyaltyTiersRewardsHistoryButtonText?.locale,
      },
    ],
    [data]
  );

  const handleTabChange = useCallback(
    (event: React.ChangeEvent, value: number) => {
      event.preventDefault();
      const route = loyaltyTabs?.[value]?.link || '';

      if (data?.loyaltyTiersRewardsHistoryButtonLink?.locale === route) {
        cdp.trackEvent({
          name: CustomEventNames.TAB,
          type: EventTypes.Other,
          attributes: {
            Name: 'Crowns History',
            Path: location.pathname,
          },
        });
        setEventPageName(EVENT_PAGE_NAMES.Rewards);
      }
      if (data?.loyaltyTiersHowItWorksButtonLink?.locale === route) {
        cdp.trackEvent({
          name: CustomEventNames.TAB,
          type: EventTypes.Other,
          attributes: {
            Name: 'How it Works',
            Path: location.pathname,
          },
        });
        setEventPageName(EVENT_PAGE_NAMES.MyBurgerKingLoyaltyTiers);
      }

      navigate(route);
    },
    [navigate, loyaltyTabs, cdp]
  );

  if (!loyaltyUser || loading) {
    return null;
  }

  return (
    <Container {...props}>
      <TabList selectedIndex={activeTab} onChange={handleTabChange}>
        {loyaltyTabs!.map((tab: any, index: number) => (
          <Tab
            key={index}
            aria-controls="loyalty-tab-selector"
            id="loyalty-tab-selector-all"
            label={tab.title}
            selected={index === activeTab}
          />
        ))}
      </TabList>
    </Container>
  );
};
